import {
  AlertTriangle,
  ArrowDownToLine,
  CalendarClock,
  CheckCircle2,
  Layers3,
  PackageSearch,
  Search,
  ShieldAlert,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  allocationExportRows,
  buildAllocationWorkbook,
  CUSTOMER_PLANS,
} from "../lib/allocation";
import { expiryClassLabel, shanghaiDateString } from "../lib/batch";
import type { DecisionPlan, ExpiryClass, WarehouseDataset } from "../types";

interface BatchesPageProps {
  dataset: WarehouseDataset;
  plan: DecisionPlan;
}

const expiryOrder: ExpiryClass[] = ["quarter", "third", "half", "fresh", "expired", "unknown"];

function downloadCsv(rows: string[][], fileName: string) {
  const escape = (value: string) => `"${value.replaceAll('"', '""')}"`;
  const content = `\uFEFF${rows.map((row) => row.map(escape).join(",")).join("\n")}`;
  const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function formatDate(value: string) {
  return value ? value.replaceAll("-", ".") : "待核对";
}

export function BatchesPage({ dataset, plan }: BatchesPageProps) {
  const [today, setToday] = useState(shanghaiDateString);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "ready" | "shortfall">("all");
  const [selectedSku, setSelectedSku] = useState("");

  useEffect(() => {
    const timer = window.setInterval(() => setToday(shanghaiDateString()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const workbook = useMemo(
    () => buildAllocationWorkbook(dataset, plan, today),
    [dataset, plan, today],
  );

  const metrics = useMemo(() => {
    const rows = allocationExportRows(workbook);
    return {
      products: workbook.products.length,
      batches: dataset.batches.length,
      fulfilled: rows.reduce((sum, row) => sum + row.fulfilled, 0),
      shortfall: rows.reduce((sum, row) => sum + row.shortfall, 0),
      backup: rows.reduce((sum, row) => sum + row.backup, 0),
    };
  }, [dataset.batches.length, workbook]);

  const filteredProducts = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return workbook.products.filter((product) => {
      const matchesQuery =
        !keyword ||
        product.product.toLowerCase().includes(keyword) ||
        product.sku.toLowerCase().includes(keyword) ||
        product.location.toLowerCase().includes(keyword);
      if (!matchesQuery) return false;
      if (statusFilter === "ready") {
        return product.plans.every((result) => result.shortfall === 0);
      }
      if (statusFilter === "shortfall") {
        return product.plans.some((result) => result.shortfall > 0);
      }
      return true;
    });
  }, [query, statusFilter, workbook.products]);

  useEffect(() => {
    if (!filteredProducts.length) {
      if (selectedSku) setSelectedSku("");
      return;
    }
    if (!filteredProducts.some((product) => product.sku === selectedSku)) {
      setSelectedSku(filteredProducts[0].sku);
    }
  }, [filteredProducts, selectedSku]);

  const selectedProduct = workbook.products.find((product) => product.sku === selectedSku);

  function exportAllocation() {
    const rows = allocationExportRows(workbook);
    downloadCsv(
      [
        [
          "数据日期",
          "SKU",
          "商品",
          "方案",
          "客户类型",
          "客户接受等级",
          "需求件数",
          "已满足",
          "缺口",
          "分配批次ID",
          "批号",
          "临期分类",
          "分配数量",
          "库位",
          "新鲜期补充",
          "警告",
        ],
        ...rows.map((row) => [
          workbook.today,
          row.sku,
          row.product,
          row.plan,
          row.customer,
          row.acceptance,
          String(row.demand),
          String(row.fulfilled),
          String(row.shortfall),
          row.batches,
          row.lots,
          row.classes,
          row.quantities,
          row.locations,
          String(row.backup),
          row.warning,
        ]),
      ],
      `仓序-全商品三档临期分配-${workbook.today}.csv`,
    );
  }

  return (
    <div className="batches-page">
      <section className="analysis-heading batches-heading">
        <div>
          <span className="section-kicker">EXPIRY-RATIO ALLOCATION</span>
          <h2>一次看清，<br />三档临期方案。</h2>
        </div>
        <p>
          ¼、⅓、½ 三档同时生成。系统按客户接受等级筛选批次，同等级内执行 FEFO；
          召回、过期和效期待核对批次不会进入自动分配。
        </p>
      </section>

      <section className="batch-storyboard batch-ratio-storyboard">
        <div className="batch-story-intro">
          <span className="story-number">Q</span>
          <div>
            <span className="section-kicker">LOT-AWARE INVENTORY</span>
            <h3>效期分级，<br />库存各归其位。</h3>
            <p>同屏对照三档客户方案，批次、数量、库位和缺口一次看全。</p>
          </div>
        </div>
        <div className="batch-story-metrics">
          <div><span>商品</span><strong>{metrics.products}</strong><small>全量商品同步计算</small></div>
          <div><span>独立批次</span><strong>{metrics.batches}</strong><small>按 SKU 与批号归档</small></div>
          <div className="safe"><span>方案满足</span><strong>{metrics.fulfilled}</strong><small>三档累计分配件数</small></div>
          <div className={metrics.shortfall ? "risk" : ""}><span>全量缺口</span><strong>{metrics.shortfall}</strong><small>需要补货或人工审批</small></div>
        </div>
      </section>

      <section className="batch-plan-overview" aria-label="三档客户方案说明">
        {CUSTOMER_PLANS.map((customerPlan) => (
          <article key={customerPlan.id}>
            <span className="batch-plan-symbol">{customerPlan.symbol}</span>
            <div>
              <strong>{customerPlan.shortLabel}</strong>
              <p>{customerPlan.customer}</p>
            </div>
            <small>{customerPlan.acceptance}</small>
          </article>
        ))}
        <div className="batch-export-panel">
          <div>
            <span className="section-kicker">ONE-TIME EXPORT</span>
            <strong>全商品 · 三档方案</strong>
            <small>新鲜期补充 {metrics.backup} 件</small>
          </div>
          <button className="primary-button" onClick={exportAllocation}>
            <ArrowDownToLine size={17} /> 一次导出全部
          </button>
        </div>
      </section>

      <section className="batch-product-workbench">
        <div className="batch-product-toolbar">
          <div>
            <span className="section-kicker">ALL PRODUCTS · LIVE DISTRIBUTION</span>
            <h3>全商品速览，三档方案同屏</h3>
          </div>
          <div className="batch-live-note">
            <CalendarClock size={15} />
            <span>按 {formatDate(today)} 实时计算临期比例</span>
          </div>
        </div>

        <div className="batch-product-controls">
          <label className="batch-product-search">
            <Search size={15} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索商品、SKU 或库位"
              aria-label="搜索商品、SKU 或库位"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="清空搜索">
                <X size={14} />
              </button>
            )}
          </label>
          <div className="batch-status-filter" aria-label="商品方案状态筛选">
            {([
              ["all", `全部 ${workbook.products.length}`],
              ["ready", "三档已满足"],
              ["shortfall", "存在缺口"],
            ] as const).map(([value, label]) => (
              <button
                type="button"
                className={statusFilter === value ? "is-active" : ""}
                onClick={() => setStatusFilter(value)}
                key={value}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="batch-product-count">当前 {filteredProducts.length} 个商品</span>
        </div>

        <div className="batch-product-catalog" aria-label="全商品三档方案速览">
          <div className="batch-catalog-head" aria-hidden="true">
            <span>商品 / SKU</span>
            <span>批次库存</span>
            <span>临期结构</span>
            <span>¼ · ⅓ · ½ 方案</span>
          </div>
          <div className="batch-catalog-scroll">
            {filteredProducts.map((product) => {
              const stockSegments = expiryOrder
                .map((expiryClass) => ({
                  expiryClass,
                  quantity: product.batches
                    .filter((batch) => batch.expiryClass === expiryClass)
                    .reduce((sum, batch) => sum + Math.max(0, batch.batch.quantity), 0),
                }))
                .filter((segment) => segment.quantity > 0);
              return (
                <button
                  type="button"
                  className={`batch-catalog-row${selectedProduct?.sku === product.sku ? " is-active" : ""}`}
                  key={product.sku}
                  onClick={() => setSelectedSku(product.sku)}
                  aria-pressed={selectedProduct?.sku === product.sku}
                >
                  <span className="batch-catalog-product">
                    <strong>{product.product}</strong>
                    <small>{product.sku} · {product.location}</small>
                  </span>
                  <span className="batch-catalog-stock">
                    <strong>{product.stock.toLocaleString("zh-CN")}</strong>
                    <small>{product.batches.length} 个批次</small>
                  </span>
                  <span className="batch-catalog-expiry">
                    <span className="batch-mini-bar" aria-hidden="true">
                      {stockSegments.map((segment) => (
                        <i
                          key={segment.expiryClass}
                          className={`is-${segment.expiryClass}`}
                          style={{ flexGrow: segment.quantity }}
                        />
                      ))}
                    </span>
                    <small>
                      {stockSegments.map((segment) => `${expiryClassLabel(segment.expiryClass)} ${segment.quantity}`).join(" · ") || "无可用库存"}
                    </small>
                  </span>
                  <span className="batch-catalog-plans">
                    {product.plans.map((result) => (
                      <span
                        className={result.shortfall ? "is-shortfall" : "is-ready"}
                        key={result.plan.id}
                        title={`${result.plan.symbol}：已满足 ${result.fulfilled}，缺口 ${result.shortfall}`}
                      >
                        {result.plan.symbol} {result.shortfall ? `缺 ${result.shortfall}` : "齐"}
                      </span>
                    ))}
                  </span>
                </button>
              );
            })}
            {!filteredProducts.length && (
              <div className="batch-catalog-empty">
                <Search size={17} />
                <span>没有符合当前搜索与状态条件的商品</span>
              </div>
            )}
          </div>
        </div>

        {selectedProduct && (
          <article className="batch-product-card batch-selected-product">
            <header className="batch-product-head">
              <div className="batch-product-copy">
                <span className="section-kicker">SELECTED PRODUCT · ALL PLANS AT ONCE</span>
                <h4>{selectedProduct.product}</h4>
                <div className="batch-product-facts">
                  <span>SKU <strong>{selectedProduct.sku}</strong></span>
                  <span>总库存 <strong>{selectedProduct.stock.toLocaleString("zh-CN")}</strong></span>
                  <span>主库位 <strong>{selectedProduct.location}</strong></span>
                  <span>批次 <strong>{selectedProduct.batches.length}</strong></span>
                </div>
              </div>

              <div className="batch-stock-visual">
                <div className="batch-stock-bar" aria-label={`${selectedProduct.product} 批次库存分布`}>
                  {expiryOrder
                    .map((expiryClass) => ({
                      expiryClass,
                      quantity: selectedProduct.batches
                        .filter((batch) => batch.expiryClass === expiryClass)
                        .reduce((sum, batch) => sum + Math.max(0, batch.batch.quantity), 0),
                    }))
                    .filter((segment) => segment.quantity > 0)
                    .map((segment) => (
                      <span
                        key={segment.expiryClass}
                        className={`is-${segment.expiryClass}`}
                        style={{ flexGrow: segment.quantity }}
                        title={`${expiryClassLabel(segment.expiryClass)}：${segment.quantity} 件`}
                      >
                        {segment.quantity}
                      </span>
                    ))}
                </div>
                <div className="batch-stock-legend">
                  {[...new Set(selectedProduct.batches.map((batch) => batch.expiryClass))].map((expiryClass) => {
                    const quantity = selectedProduct.batches
                      .filter((batch) => batch.expiryClass === expiryClass)
                      .reduce((sum, batch) => sum + Math.max(0, batch.batch.quantity), 0);
                    return (
                      <span key={expiryClass}>
                        <i className={`is-${expiryClass}`} />
                        {expiryClassLabel(expiryClass)} {quantity}
                      </span>
                    );
                  })}
                </div>
              </div>
            </header>

            <div className="batch-plan-grid">
              {selectedProduct.plans.map((result) => (
                <section className="batch-plan-card" key={result.plan.id}>
                  <header>
                    <div>
                      <span className="batch-plan-badge">{result.plan.symbol}</span>
                      <div>
                        <strong>{result.plan.shortLabel}</strong>
                        <small>{result.plan.customer}</small>
                      </div>
                    </div>
                    <span className={result.shortfall ? "batch-plan-state is-warning" : "batch-plan-state"}>
                      {result.shortfall ? <AlertTriangle size={13} /> : <CheckCircle2 size={13} />}
                      {result.shortfall ? "有缺口" : "已满足"}
                    </span>
                  </header>

                  <div className="batch-plan-metrics">
                    <div><span>需求</span><strong>{result.demand}</strong></div>
                    <div><span>已满足</span><strong>{result.fulfilled}</strong></div>
                    <div className={result.shortfall ? "is-shortfall" : ""}><span>缺口</span><strong>{result.shortfall}</strong></div>
                  </div>

                  <div className="batch-allocation-scroll">
                    <div className="batch-allocation-table" role="table" aria-label={`${selectedProduct.product} ${result.plan.symbol} 方案分配`}>
                      <div className="batch-allocation-row batch-allocation-head" role="row">
                        <span role="columnheader">批次</span>
                        <span role="columnheader">临期分类</span>
                        <span role="columnheader">数量</span>
                        <span role="columnheader">库位</span>
                      </div>
                      {result.lines.map((line) => (
                        <div className="batch-allocation-row" role="row" key={`${result.plan.id}-${line.batchId}`}>
                          <span className="batch-lot-cell" role="cell">
                            <strong>{line.lot}</strong>
                            <small>{formatDate(line.expiresOn)}</small>
                          </span>
                          <span className={`expiry-class-tag is-${line.expiryClass}`} role="cell">
                            {expiryClassLabel(line.expiryClass)}
                          </span>
                          <span className="batch-quantity-cell" role="cell">{line.allocated} 件</span>
                          <span className="location-chip" role="cell">{line.location}</span>
                        </div>
                      ))}
                      {!result.lines.length && (
                        <div className="batch-allocation-empty">
                          <PackageSearch size={17} />
                          <span>没有符合该客户接受等级的可用批次</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <footer>
                    <span><Sparkles size={13} /> 新鲜期补充 {result.backupAllocated} 件</span>
                    {result.warnings.length ? (
                      <p><ShieldAlert size={13} /> {result.warnings.join(" ")}</p>
                    ) : (
                      <p>按接受等级筛选，同等级内最早到期优先。</p>
                    )}
                  </footer>
                </section>
              ))}
            </div>
          </article>
        )}

        {!workbook.products.length && (
          <div className="empty-state batch-empty-state">
            <Layers3 size={24} />
            <strong>没有可用于分配的商品批次</strong>
            <p>请导入包含 SKU、批号、数量、到期日及可选生产日期/保质期天数的数据。</p>
          </div>
        )}
      </section>

      <section className="batch-method-strip">
        <div><span>01</span><strong>排除风险批次</strong><p>召回、过期和效期待核对批次冻结，不进入自动履约。</p></div>
        <div><span>02</span><strong>匹配客户接受等级</strong><p>按 ¼、⅓、½ 三档同时筛选，客户需求一次看清。</p></div>
        <div><span>03</span><strong>同等级内 FEFO</strong><p>最早到期批次优先，新鲜期仅在满足等级后补充。</p></div>
      </section>
    </div>
  );
}
